//
// Copyright 2020 DXOS.org
//

import React, { Suspense } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import { toPublicKey } from '@dxos/protocols/buf';
import { DeviceKind, useDevices, useIdentity } from '@dxos/react-client/halo';
import * as Icon from '@dxos/react-ui/Icon';
import * as ScrollArea from '@dxos/react-ui/ScrollArea';
import * as Status from '@dxos/react-ui/Status';
import { mx } from '@dxos/ui-theme';

import { useSections } from '../hooks/index.ts';

export const RootContainer = () => {
  const { pathname } = useLocation();

  return (
    <div className='dx-expand flex'>
      <Sidebar />
      <div className='flex flex-col grow overflow-hidden'>
        <Status.ErrorBoundary key={pathname} name='devtools.root'>
          <Suspense>
            <Outlet />
          </Suspense>
        </Status.ErrorBoundary>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const { pathname } = useLocation();
  const sections = useSections();
  return (
    <ScrollArea.Root orientation='vertical' classNames='w-[180px] border-e border-separator'>
      <ScrollArea.Viewport classNames='gap-4 divide-y divide-separator-subtle'>
        {sections.map((section) => (
          <div key={section.id}>
            <div className='flex text-sm ps-4 py-1'>{section.title}</div>
            <div>
              {section.items?.map(({ id, title, icon }) => (
                <div key={id} className={mx('flex items-center ps-4 gap-2', id === pathname && 'bg-current-surface')}>
                  <Icon.Icon icon={icon} />
                  <Link to={id} className='grow'>
                    <span>{title}</span>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className='grow' />
        <Footer />
      </ScrollArea.Viewport>
    </ScrollArea.Root>
  );
};

const Footer = () => {
  const identity = useIdentity();
  const devices = useDevices();
  const device = devices.find(({ kind }) => kind === DeviceKind.CURRENT);

  return (
    <div className='flex p-2'>
      <div className='flex flex-col w-full text-sm text-neutral-500'>
        <div className='grid grid-cols-2 gap-2'>
          <div className='text-neutral-300 text-right'>Identity</div>
          <div className='font-mono'>{toPublicKey(identity?.identityKey)?.truncate()}</div>
        </div>
        <div className='grid grid-cols-2 gap-2'>
          <div className='text-neutral-300 text-right'>Device</div>
          <div className='font-mono'>{toPublicKey(device?.deviceKey)?.truncate()}</div>
        </div>
        <div className='grid grid-cols-2 gap-2'>
          <div className='text-neutral-300 text-right'>Version</div>
          <div className='font-mono'>{process.env.PACKAGE_VERSION ?? 'DEV'}</div>
        </div>
      </div>
    </div>
  );
};
